import type { EnableStatus } from "@/types";
import type { PageData } from "@/types/page";

/** 门店类型：直营 / 加盟 / 合作 */
export type ShopType = "DIRECT" | "FRANCHISE" | "COOPERATION";

/** 门店状态：ENABLED 启用 / DISABLED 停用；另含未激活 / 已过期 */
export type ShopStatus = EnableStatus | "NONACTIVE" | "EXPIRED";

/** 门店规模：SMALL / MEDIUM / LARGE */
export type ShopScale = "SMALL" | "MEDIUM" | "LARGE";

/** 门店 WIFI（对应后端 ShopWifiItem） */
export interface ShopWifiItem {
  /** WIFI 名称 */
  ssid: string;
  /** WIFI 密码 */
  password?: string;
}

/** 门店分页查询请求（对应后端 getPage Query） */
export interface ShopPageQueryRequest {
  /** 门店 ID */
  shopId?: string;
  /** 门店编号精确匹配 */
  shopCode?: string;
  /** 门店名称模糊匹配 */
  shopName?: string;
  /** 门店类型：DIRECT / FRANCHISE / COOPERATION */
  shopType?: ShopType;
  /** 门店状态：ENABLED / DISABLED */
  status?: ShopStatus;
  /** 归属代理 ID（兼容旧字段） */
  agentId?: string;
  /** 归属上级组织 ID */
  parentOrgId?: string;
  /** 归属连锁 ID；有值时优先于 agentId/parentOrgId */
  chainId?: string;
  /** 归属品牌标签 ID */
  brandTagId?: string;
  /** 门店规模：SMALL / MEDIUM / LARGE */
  scale?: ShopScale;
  /** 关键字全文检索（有值走 ES） */
  keyword?: string;
  /** 仅查合同/授权即将到期（30 天内） */
  expireWarningOnly?: boolean;
  /** 页码 */
  pageNum?: number;
  /** 每页条数 */
  pageSize?: number;
}

/** 门店分页列表项（对应后端 ShopPageItemVO） */
export interface ShopPageItemVO {
  /** 门店 ID */
  id: string;
  /** 门店编号 */
  shopCode: string;
  /** 门店全称 */
  shopName: string;
  /** 门店简称 */
  shopShortName: string;
  /** 门店类型 */
  shopType: ShopType;
  /** 门店类型名称 */
  shopTypeLabel: string;
  /** 门店状态 */
  status: ShopStatus;
  /** 所在地区 */
  locationPath: string;
  /** 授权状态 */
  authStatus: string;
  /** 授权规模名称 */
  authScaleLabel?: string;
  /** 门店规模：SMALL / MEDIUM / LARGE */
  scale?: ShopScale;
  /** 归属上级组织 ID */
  parentOrgId?: string;
  /** 归属上级组织类型 */
  parentOrgType?: string;
  /** 归属代理 ID（兼容旧字段，等同 parentOrgId） */
  agentId?: string;
  /** 归属上级/代理名称 */
  agentName?: string;
  /** 关联工商实体 ID */
  businessEntityId?: string;
  /** 关联工商实体名称 */
  businessEntityName?: string;
  /** 门店管理员用户 ID */
  userId?: string;
  /** 是否连锁总部：0 否 / 1 是 */
  isChainHq?: number;
  /** 门店管理员名称 */
  userName?: string;
  /** 合同到期日期 */
  contractExpireAt?: string;
  /** 授权到期日期 */
  authEndAt?: string;
  /** 合同到期预警 */
  contractExpireWarning?: boolean;
  /** 授权到期预警 */
  authExpireWarning?: boolean;
  /** 创建时间 */
  createAt?: string;
  /** 绑定 GAMING_PC 数量 */
  gamingPcCount?: number;
  /** 品牌标签 ID */
  brandTagId?: string;
  /** 品牌名称 */
  brandTagName?: string;
}

/** 门店分页数据（对应后端 PageDataShopPageItemVO） */
export type PageDataShopPageItemVO = PageData<ShopPageItemVO>;

/** 门店银行账户与开票信息（对应后端 ShopBankAccountPart） */
export interface ShopBankAccountPart {
  /** 收款名称 */
  accountName?: string;
  /** 收款卡号明文（入库加密） */
  accountNo?: string;
  /** 开户银行 */
  bankName?: string;
  /** 发票抬头 */
  invoiceTitle?: string;
  /** 纳税人识别号 */
  taxId?: string;
}

/** 合同与授权子结构（对应后端 ShopContractPart） */
export interface ShopContractPart {
  /** 合同签订日期 */
  contractSignedAt?: string;
  /** 合同到期日期 */
  contractExpireAt?: string;
  /** 合同附件 OSS URL */
  contractFileUrl?: string;
  /** 授权起始日期 */
  authStartAt?: string;
  /** 授权到期日期 */
  authEndAt?: string;
  /** 授权终端数量 */
  authDeviceQuantity?: number;
  /** 终端单价（元/台/年） */
  deviceUnitPrice?: number;
  /** 打印机型号 */
  printerModel?: string;
  /** 打印机设备编号 */
  printerDeviceNo?: string;
}

/** 门店详情（对应后端 ShopDetailVO） */
export interface ShopDetailVO {
  /** 门店 ID */
  id: string;
  /** 门店编号 */
  shopCode: string;
  /** 门店全称 */
  shopName: string;
  /** 门店简称 */
  shopShortName: string;
  /** 门店类型 */
  shopType: ShopType;
  /** 门店类型名称 */
  shopTypeLabel: string;
  /** 品牌标签 ID */
  brandTagId?: string;
  /** 品牌名称 */
  brandTagName?: string;
  /** 品牌标签 ID 列表（兼容旧字段） */
  brandTags?: string[];
  /** 门店状态 */
  status: ShopStatus;
  /** 所在地区 */
  locationPath?: string;
  /** 地区编码 */
  regionCodes?: string[];
  /** 详细地址 */
  detailedAddress?: string;
  /** 纬度 */
  latitude?: number;
  /** 经度 */
  longitude?: number;
  /** 手机号码 */
  phone?: string;
  /** 网吧电话 */
  landline?: string;
  /** WIFI 列表 */
  wifiList?: ShopWifiItem[];
  /** 区域标签 */
  regionTags?: string[];
  /** 是否超出代理范围：0 否 / 1 是 */
  beyondAgentRange?: number;
  /** 门店面积（㎡） */
  areaSqm?: number;
  /** 营业时间 */
  businessHours?: string;
  /** 备注 */
  remark?: string;
  /** 门店 Logo OSS URL 列表 */
  shopLogo?: string[];
  /** 门头照 OSS URL 列表 */
  doorPhotoUrl?: string[];
  /** 营业场所照 OSS URL 列表 */
  premisePhotoUrl?: string[];
  /** 平面图 OSS URL 列表 */
  floorPlanUrl?: string[];
  /** 会员数量（查询时统计） */
  memberCount?: number;
  /** 员工数量（查询时统计） */
  employeeCount?: number;
  /** 归属上级组织 ID */
  parentOrgId?: string;
  /** 归属上级组织类型：AGENT / PLATFORM 等 */
  parentOrgType?: string;
  /** 归属代理 ID（兼容旧字段） */
  agentId?: string;
  /** 归属上级/代理名称 */
  agentName?: string;
  /** 工商实体 ID */
  businessEntityId?: string;
  /** 工商实体名称 */
  businessEntityName?: string;
  /** 门店管理员用户 ID */
  userId?: string;
  /** 是否连锁总部：0 否 / 1 是 */
  isChainHq?: number;
  /** 门店管理员名称 */
  managerUserName?: string;
  /** 合同编号 */
  contractNo?: string;
  /** 合同签订日期 */
  contractSignedAt?: string;
  /** 合同到期日期 */
  contractExpireAt?: string;
  /** 合同附件 */
  contractFileUrl?: string;
  /** 授权状态 */
  authStatus?: string;
  /** 授权状态名称 */
  authStatusLabel?: string;
  /** 授权起始日期 */
  authStartAt?: string;
  /** 授权到期日期 */
  authEndAt?: string;
  /** 授权终端数量 */
  authDeviceQuantity?: number;
  /** 终端单价 */
  deviceUnitPrice?: number;
  /** 授权费用 */
  authFee?: number;
  /** 授权规模 */
  authScale?: string;
  /** 授权规模名称 */
  authScaleLabel?: string;
  /** 打印机型号 */
  printerModel?: string;
  /** 打印机设备编号 */
  printerDeviceNo?: string;
  /** 收款名称 */
  accountName?: string;
  /** 收款卡号 */
  accountNo?: string;
  /** 开户银行 */
  bankName?: string;
  /** 发票抬头 */
  invoiceTitle?: string;
  /** 纳税人识别号 */
  taxId?: string;
  /** 合同到期预警（30 天内） */
  contractExpireWarning?: boolean;
  /** 授权到期预警（30 天内） */
  authExpireWarning?: boolean;
  /** 创建时间 */
  createAt?: string;
  /** 更新时间 */
  updateAt?: string;
}

/** 更新门店请求（对应后端 ShopUpdateRequest，不含合同授权） */
export interface ShopUpdateRequest {
  /** 门店全称 */
  shopName?: string;
  /** 门店简称 */
  shopShortName?: string;
  /** 门店类型：DIRECT / FRANCHISE / COOPERATION */
  shopType?: ShopType;
  /** 品牌标签 ID */
  brandTagId?: string;
  /** 所在地区 */
  locationPath?: string;
  /** 地区编码 */
  regionCodes?: string[];
  /** 详细地址 */
  detailedAddress?: string;
  /** 手机号码 */
  phone?: string;
  /** 网吧电话 */
  landline?: string;
  /** WIFI 列表 */
  wifiList?: ShopWifiItem[];
  /** 纬度 */
  latitude?: number;
  /** 经度 */
  longitude?: number;
  /** 区域标签 */
  regionTags?: string[];
  /** 门店面积（㎡） */
  areaSqm?: number;
  /** 营业时间 */
  businessHours?: string;
  /** 备注 */
  remark?: string;
  /** 门店 Logo OSS URL 列表 */
  shopLogo?: string[];
  /** 门头照 OSS URL 列表 */
  doorPhotoUrl?: string[];
  /** 营业场所照 OSS URL 列表 */
  premisePhotoUrl?: string[];
  /** 平面图 OSS URL 列表 */
  floorPlanUrl?: string[];
  /** 归属上级组织 ID */
  parentOrgId?: string;
  /** 归属上级组织类型 */
  parentOrgType?: string;
  /** 归属代理 ID（兼容旧字段） */
  agentId?: string;
  /** 关联工商实体 ID；传 null 表示不修改，传 0 表示解绑 */
  businessEntityId?: string | null;
  /** 门店管理员用户 ID */
  userId?: string;
  /** 银行账户与开票信息 */
  bankAccount?: ShopBankAccountPart;
}

/** 更新门店合同授权请求（对应后端 ShopContractUpdateRequest） */
export type ShopContractUpdateRequest = ShopContractPart;

/** 门店状态变更请求（对应后端 ShopStatusUpdateRequest） */
export interface ShopStatusUpdateRequest {
  /** 门店状态：ENABLED / DISABLED */
  status: EnableStatus;
}

/** 新增门店请求（对应后端 ShopCreateRequest） */
export interface ShopCreateRequest {
  /** 门店邀请 ID，来自邀请流程时传入 */
  invitationId?: string;
  /** 门店全称 */
  shopName: string;
  /** 门店简称 */
  shopShortName?: string;
  /** 门店类型：DIRECT / FRANCHISE / COOPERATION */
  shopType: string;
  /** 品牌标签 ID */
  brandTagId?: string;
  /** 所在地区 */
  locationPath?: string;
  /** 地区编码 */
  regionCodes?: string[];
  /** 详细地址 */
  detailedAddress?: string;
  /** 纬度 */
  latitude?: number;
  /** 经度 */
  longitude?: number;
  /** 区域标签 */
  regionTags?: string[];
  /** 门店面积（㎡） */
  areaSqm?: number;
  /** 营业时间 */
  businessHours?: string;
  /** 备注 */
  remark?: string;
  /** 门店 Logo OSS URL 列表 */
  shopLogo?: string[];
  /** 门头照 OSS URL 列表 */
  doorPhotoUrl?: string[];
  /** 营业场所照 OSS URL 列表 */
  premisePhotoUrl?: string[];
  /** 平面图 OSS URL 列表 */
  floorPlanUrl?: string[];
  /** 归属上级 ID */
  parentOrgId: string;
  /** 归属上级类型 */
  parentOrgType?: string;
  /** 关联工商实体 ID */
  businessEntityId?: string;
  /** 门店绑定用户 ID */
  userId: string;
  /** 合同与授权信息 */
  contract?: ShopContractPart;
  /** 银行账户与开票信息 */
  bankAccount?: ShopBankAccountPart;
}

/** 预注册未激活门店请求（对应后端 ShopPreRegisterRequest） */
export interface ShopPreRegisterRequest {
  /** 负责人手机号 */
  phone: string;
  /** 门店类型：DIRECT / FRANCHISE / COOPERATION */
  shopType: string;
  /** 归属上级组织 ID（代理 ID） */
  parentOrgId?: string;
  /** 归属上级组织类型：AGENT / PLATFORM 等 */
  parentOrgType?: string;
  /** 归属品牌 ID */
  brandId?: string;
  /** 授权开始日期 */
  authStartDate?: string;
  /** 授权结束日期 */
  authEndDate?: string;
  /** 授权终端数 */
  authorizedTerminals?: number;
  /** 授权终端单价 */
  terminalUnitPrice?: number;
  /** 合同附件 URL 列表 */
  contractAttachments?: string[];
  /** 备注 */
  remark?: string;
}

/** 按品牌标签统计门店数量请求（对应后端 BrandTagCountShopsRequest） */
export interface BrandTagCountShopsRequest {
  /** 品牌标签 ID 列表 */
  brandTagIds: string[];
}

/** 品牌标签关联门店数量项（对应后端 BrandTagCountItemVO） */
export interface BrandTagCountItemVO {
  /** 品牌标签 ID */
  brandTagId: string;
  /** 关联门店数量 */
  storeCount: number;
}

/** 代理 ID 批量查询请求（对应后端 AgentBatchIdsRequest） */
export interface AgentBatchIdsRequest {
  /** 代理 ID 列表 */
  agentIds: string[];
}

/** 代理下辖门店数统计项（对应后端 AgentShopCountItemVO） */
export interface AgentShopCountItemVO {
  /** 代理 ID */
  agentId: string;
  /** 下辖门店数 */
  shopCount: number;
  /** 下辖员工数 */
  employeeCount?: number;
}

/** 门店绑定 PC 分页查询（对应后端设备 getPage） */
export interface OpsDevicePageQueryRequest {
  /** 门店 ID */
  shopId: string;
  /** 设备状态 dict_code（deviceStatus）；空=全部 */
  deviceStatus?: string;
  /** 关键词（设备编码/MAC/IP） */
  keyword?: string;
  /** 页码 */
  pageNum?: number;
  /** 每页条数 */
  pageSize?: number;
}

/** 门店绑定 PC 分页列表项（对应后端 OpsDevicePageVO） */
export interface OpsDevicePageVO {
  /** 设备 ID */
  id: string;
  /** 机位 ID（int64；有进行中会话时有值） */
  machineId?: string;
  /** 会话 ID（int64；有进行中会话时有值，结账必填） */
  sessionId?: string;
  /** PC 编码（设备编码） */
  deviceCode?: string;
  /** MAC 地址 */
  macAddress?: string;
  /** IP 地址 */
  ipAddress?: string;
  /** 设备状态 dict_code（在线/离线/停用等） */
  deviceStatus?: string;
  /** 心跳状态（本期占位，可能为 null） */
  heartbeatStatus?: string | null;
  /** 心跳时间/时长文案（本期占位，可能为 null） */
  heartbeatDesc?: string | null;
}

/** 门店绑定 PC 分页数据 */
export type PageDataOpsDevicePageVO = PageData<OpsDevicePageVO>;

/**
 * 取证照/图片字段的首个 OSS URL（后端多为 string[]）
 */
export const pickFirstOssUrl = (value?: string | string[] | null): string | undefined => {
  if (!value) return undefined;
  if (Array.isArray(value)) return value.find(Boolean);
  return value || undefined;
};
